import { graphql, useStaticQuery } from "gatsby"
import React, { useState, useEffect, useRef } from "react"
import FilterBar from "components/FilterBar"
import { SEOHead } from "components/SEO"
import useTutorialAccess from "effects/useTutorialAccess"
import parseYoutubeId from "lib/validateYoutubeUrl"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"
import TutorialCard from "components/TutorialCard"
import TutorialModal from "components/TutorialModal"
import Layout from "components/ui/Layout"
import * as css from "components/pages/tutorials/Tutorials.module.css"

const TUTORIAL_FILTERS = [
  { id: null, label: "All" },
  { id: "Feature Highlights", label: "Feature Highlights" },
  { id: "Live Demos", label: "Live Demos" },
]

function TutorialsPage({ location: { pathname, search } }) {
  const [activeCategory, setActiveCategory] = useState("Feature Highlights")
  const [selectedTutorial, setSelectedTutorial] = useState(null)
  const { hasAccess, isLoading, grantAccess } = useTutorialAccess()
  // Whether the currently-open modal resulted from a pushState *we* just
  // did (grid click, related-video click) as opposed to a deep link or a
  // popstate landing. Closing can safely call history.back() in the first
  // case; in the second there's nothing of ours to pop, so back() would
  // send the visitor off-site (or to whatever page referred them here).
  const openedViaPushRef = useRef(false)

  const data = useStaticQuery(graphql`
    {
      allTutorialsYaml(sort: { order: ASC }) {
        nodes {
          id
          title
          slug
          author
          category
          duration
          videoUrl
          youtubeId
          description
          date(formatString: "MMMM D, YYYY")
          thumbnail {
            url
            alt
          }
          order
          relatedVideos
        }
      }
      allEventsYaml(sort: { date: DESC }) {
        nodes {
          id
          title
          slug
          author
          type
          videoUrl
          description
          date
          thumbnail {
            url
            alt
          }
        }
      }
    }
  `)

  // Auto-synthesize Live Demos from past live-demo events that have a YouTube URL
  const today = new Date()
  const liveDemos = data.allEventsYaml.nodes
    .filter(
      (e) =>
        e.type !== "conference" &&
        e.videoUrl &&
        parseYoutubeId(e.videoUrl) &&
        new Date(e.date) < today
    )
    .map((e) => ({
      id: `event-${e.id}`,
      title: e.title,
      slug: e.slug,
      // Events have no structured speaker field today, so this is usually
      // empty — AuthorAvatars renders nothing. Set `author` on an event
      // (e.g. "Jane Doe & John Roe") to surface presenter avatars on its card.
      author: e.author,
      category: "Live Demos",
      youtubeId: parseYoutubeId(e.videoUrl),
      description: e.description,
      date: e.date
        ? new Date(e.date).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : null,
      thumbnail: e.thumbnail,
      relatedVideos: [],
    }))

  const tutorials = [...data.allTutorialsYaml.nodes, ...liveDemos]

  // Deep link: ?watch={slug} opens the matching tutorial modal on load
  useEffect(() => {
    if (!search) return
    const watchSlug = new URLSearchParams(search).get("watch")
    if (!watchSlug) return
    const target = tutorials.find((t) => t.slug === watchSlug)
    if (target) {
      setSelectedTutorial(target)
      setActiveCategory(target.category)
    }
  }, [])

  // Keep the modal in sync with browser back/forward navigation, since
  // opening/closing a video pushes a history entry (see handleOpenTutorial
  // and handleCloseModal below).
  useEffect(() => {
    const handlePopState = () => {
      const watchSlug = new URLSearchParams(window.location.search).get("watch")
      const target = watchSlug
        ? tutorials.find((t) => t.slug === watchSlug)
        : null
      // We just landed here via browser navigation, not our own pushState —
      // treat it the same as a deep link for the purposes of closing.
      openedViaPushRef.current = false
      setSelectedTutorial(target || null)
      if (target) setActiveCategory(target.category)
    }
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  // Filter by category
  const filteredTutorials = activeCategory
    ? tutorials.filter((t) => t.category === activeCategory)
    : tutorials

  const handleOpenTutorial = (tutorial) => {
    setSelectedTutorial(tutorial)
    const url = new URL(window.location.href)
    url.searchParams.set("watch", tutorial.slug)
    window.history.pushState({}, "", url)
    openedViaPushRef.current = true
  }

  const handleCloseModal = () => {
    setSelectedTutorial(null)
    if (openedViaPushRef.current) {
      // Pop the entry we pushed on open, rather than pushing yet another
      // one — keeps back/forward mapped 1:1 to what the visitor actually
      // did instead of growing the stack by two entries per video.
      openedViaPushRef.current = false
      window.history.back()
    } else {
      const url = new URL(window.location.href)
      url.searchParams.delete("watch")
      window.history.replaceState({}, "", url)
    }
  }

  return (
    <Layout
      headerFloating
      trackingName="Tutorials"
      pageContentType="listing-page"
      pageName="tutorials"
      pagePath={pathname}
    >
      <Section className={css.hero}>
        <SimpleHeader
          level="h1"
          label="Learn how to get the most out of Preset"
          title="Tutorials"
          titleSize="xxl"
          dash="horizontal"
        />
      </Section>

      <Section className={css.listingSection}>
        <FilterBar
          filters={TUTORIAL_FILTERS}
          activeFilter={activeCategory}
          onChange={setActiveCategory}
          className={css.categoryFilter}
        />

        {filteredTutorials.length > 0 ? (
          <div className={css.grid}>
            {filteredTutorials.map((tutorial) => (
              <TutorialCard
                key={tutorial.id}
                title={tutorial.title}
                author={tutorial.author}
                category={tutorial.category}
                duration={tutorial.duration}
                thumbnail={tutorial.thumbnail}
                onClick={() => handleOpenTutorial(tutorial)}
              />
            ))}
          </div>
        ) : (
          <div className={css.noResults}>
            <p>No tutorials found in this category.</p>
          </div>
        )}
      </Section>

      {selectedTutorial && (
        <TutorialModal
          tutorial={selectedTutorial}
          allTutorials={tutorials}
          hasAccess={hasAccess}
          isLoading={isLoading}
          onGrantAccess={grantAccess}
          onClose={handleCloseModal}
          onSelectTutorial={handleOpenTutorial}
        />
      )}
    </Layout>
  )
}

export function Head() {
  return (
    <SEOHead
      title="Video Tutorials - Learn Preset and Apache Superset"
      description="Watch free video tutorials to learn how to build dashboards, visualize data, and get the most out of Preset and Apache Superset."
    />
  )
}

export default TutorialsPage
